import React, { useEffect, useState } from 'react'
import { usePath } from '@resultadosdigitais/front-hub/react'
import styled from 'styled-components/macro'

import Form from 'components/Form'

import { Button, ButtonGroup, Text } from '@resultadosdigitais/tangram-components'
import { Trans, useTranslation } from 'react-i18next'
import loginService from '../../login-service'
import { buildFaqSecurityAlertURL } from 'services/urlsTranslations'
import MFAappChallengeImage from './email_code.svg'
import DeviceApprovalChallengeCode from 'components/DeviceApprovalChallengeCode'

const StyledForm = styled(Form)`
  width: auto !important;
`
const StyledImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding-bottom: var(--size-spacing-02);
`

const StyledImage = styled.img`
  height: auto !important;
`

const DeviceApprovalChallengeSection = styled.section`
  padding-top: var(--size-spacing-05);
  display: grid;
`
const StyledButtonGroup = styled(ButtonGroup)`
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  gap: var(--size-spacing-03);
`

const StyledText = styled(Text)`
  margin-bottom: 0;
  margin-top: 0;
  & a {
    text-decoration: none;
    color: var(--color-primary-70);
    font-weight: 700;
  }
`
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(--size-spacing-04);
  padding: 0;
`

const Header = styled.header``
const FaqContainer = styled.div`
  margin: var(--size-spacing-05) 0px var(--size-spacing-02) 0px;
`
const ExpirationContainer = styled.div``
const MINIMUM_NUMBER_CODE = 6

const maskEmail = email => {
  const [localPart, domainPart] = email.split('@')
  const maskedLocalPart = `${localPart.substring(0, 2)}*******`
  const lastDotIndex = domainPart.lastIndexOf('.')
  const domainToShow = domainPart.substring(0, Math.min(4, lastDotIndex))
  const tld = domainPart.substring(lastDotIndex)
  const maskedDomainPart = `${domainToShow}********${tld}`

  return `${maskedLocalPart}@${maskedDomainPart}`
}

export default function DeviceRegistrationForm({ email, goToLoginForm }) {
  const maskedEmail = maskEmail(email)
  const { t, i18n } = useTranslation()
  const assetPath = usePath()
  const [code, setCode] = useState()
  const [hasInvalidCodeError, setHasInvalidCodeError] = useState(false)
  const [hasBackendError, setHasBackendError] = useState(false)
  const [resetCode, setResetCode] = useState(false)
  const shouldNextButtonDisabled = !code || code.length < MINIMUM_NUMBER_CODE
  const [isResendCodeEnabled, setIsResendCodeEnabled] = useState(true)
  const RESEND_CODE_COUNT = 45
  const [resendCodeCount, setResendCodeCount] = useState(RESEND_CODE_COUNT)
  const [firstResendAttemptMade, setFirstResendAttemptMade] = useState(false) // New state to track if the first resend attempt has been made
  const resendButton = t('DeviceRegistrationForm.ResendCodeButton')
  useEffect(() => {
    let interval
    if (isResendCodeEnabled && resendCodeCount > 0) {
      interval = setInterval(() => {
        setResendCodeCount(prevCount => prevCount - 1)
      }, 1000)
    } else if (resendCodeCount === 0) {
      clearInterval(interval)
      setIsResendCodeEnabled(false)
    }
    return () => clearInterval(interval)
  }, [isResendCodeEnabled, resendCodeCount])

  const handleGoBack = () => goToLoginForm && goToLoginForm()
  const handleResendCode = async () => {
    setFirstResendAttemptMade(true)
    setIsResendCodeEnabled(true)
    setResendCodeCount(RESEND_CODE_COUNT)
    try {
      await loginService.resendDeviceApprovalCode()
    } catch (e) {
      console.error(e)
    }
  }

  const submitForm = async () => {
    try {
      if (code.length === 6) {
        const { data } = await loginService.deviceApprovalLogin({
          code: code,
        })
        setHasInvalidCodeError(false)
        setHasBackendError(false)
        window.location.assign(data.redirectTo)
      }
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error === 'invalid_code') {
        setHasInvalidCodeError(true)
        setHasBackendError(false)
      } else {
        setHasInvalidCodeError(false)
        setHasBackendError(true)
      }
      console.error(e)
      setResetCode(true)
    }
  }

  return (
    <StyledForm>
      <StyledImageContainer>
        <StyledImage src={assetPath(MFAappChallengeImage)} alt={t('DeviceRegistrationForm.Acessibility.DeviceSuccesAlt')} />
      </StyledImageContainer>
      <DeviceApprovalChallengeSection>
        <Header>
          <StyledText token={Text.tokens.TEXT_XL_BOLD} textColor="--color-gray-100">
            {t('DeviceRegistrationForm.Title')}
          </StyledText>
        </Header>
        <FormContainer>
          <StyledText token={Text.tokens.TEXT_MD_REGULAR} textColor="--color-gray-100">
            <Trans i18nKey="DeviceRegistrationForm.Description" values={{ maskedEmail }} components={[]} />
          </StyledText>
          <DeviceApprovalChallengeCode
            hasError={hasInvalidCodeError || hasBackendError}
            onChange={value => setCode(value)}
            onSend={submitForm}
            reset={resetCode}
            resetCallback={() => setResetCode(false)}
          />
          {hasInvalidCodeError && (
            <Text textColor="--danger-text">
              <Trans i18nKey="DeviceRegistrationForm.ErrorMessageCodeInvalid" values={{ resendButton }} components={[]} />
            </Text>
          )}
          {hasBackendError && <Text textColor="--danger-text">{t('DeviceRegistrationForm.ErrorMessage')}</Text>}
        </FormContainer>
        <ExpirationContainer>
          <StyledText token={Text.tokens.TEXT_SM_REGULAR} textColor="--neutral-text-low-emphasis">
            {t('DeviceRegistrationForm.ExpirationCodeText')}
          </StyledText>
        </ExpirationContainer>
        <FaqContainer>
          <StyledText token={Text.tokens.TEXT_MD_REGULAR} textColor="--color-gray-100">
            <Trans
              i18nKey="DeviceRegistrationForm.Faq"
              components={[
                <a href={buildFaqSecurityAlertURL(i18n.language)} target="_blank" rel="noopener noreferrer">
                  .
                </a>,
              ]}
            />
          </StyledText>
        </FaqContainer>
      </DeviceApprovalChallengeSection>
      <StyledButtonGroup>
        <Button onClick={handleGoBack} kind={Button.kinds.tertiary}>
          {t('DeviceRegistrationForm.BackButton')}
        </Button>
        <Button
          disabled={firstResendAttemptMade ? isResendCodeEnabled || resendCodeCount > 0 : false}
          onClick={handleResendCode}
          kind={Button.kinds.secondary}
        >
          {firstResendAttemptMade && resendCodeCount > 0
            ? `${t('DeviceRegistrationForm.ResendCodeButton')} (${resendCodeCount})`
            : t('DeviceRegistrationForm.ResendCodeButton')}
        </Button>
        <Button disabled={shouldNextButtonDisabled} onClick={submitForm}>
          {t('DeviceRegistrationForm.NextButton')}
        </Button>
      </StyledButtonGroup>
    </StyledForm>
  )
}
